import { useState, useEffect, useRef, useCallback } from 'react'
import QRCode from 'qrcode'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, CircularProgress,
} from '@mui/material'
import { Download } from '@mui/icons-material'

interface QrCodeDialogProps {
  open: boolean
  onClose: () => void
  title: string
  content: string
}

export function QrCodeDialog({ open, onClose, title, content }: QrCodeDialogProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const generateQr = useCallback(async () => {
    if (!canvasRef.current || !content) return
    setLoading(true)
    setError('')
    try {
      await QRCode.toCanvas(canvasRef.current, content, {
        width: 256,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' },
      })
    } catch {
      setError('Failed to generate QR Code. Content may be too long.')
    } finally {
      setLoading(false)
    }
  }, [content])

  useEffect(() => {
    if (open) generateQr()
  }, [open, generateQr])

  const handleDownload = useCallback(() => {
    if (!canvasRef.current) return
    const link = document.createElement('a')
    link.download = `qrcode-${title.toLowerCase().replace(/\s+/g, '-')}.png`
    link.href = canvasRef.current.toDataURL('image/png')
    link.click()
  }, [title])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>QR Code - {title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
          {loading && <CircularProgress />}
          {error && <Typography color="error">{error}</Typography>}
          <canvas ref={canvasRef} style={{ display: error ? 'none' : 'block' }} />
          <Box sx={{ width: '100%', bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              Content:
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.5 }}>
              {content}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDownload} startIcon={<Download />} disabled={!!error}>
          Download PNG
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
